/* eslint-disable sorare/no-detached-fragments */
import { TypedDocumentNode, gql } from '@apollo/client';

import type { StuffInRewardBoxType } from '@sorare/core/src/components/rewards/RewardBoxDialog/types';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import { withFragments } from '@sorare/core/src/lib/gql';
import {
  BOX_COLOR,
  BOX_LIGHT_INTENSITY,
  FRAME_COLOR,
} from '@sorare/core/src/lib/probabilisticBundle';

import type { RewardBox } from '@sorare/trois/src/components/ClaimRewardBoxes/type';
import { UNOPENED_REWARD_BOXES } from 'layouts/my-cards/MyCardsLayout';

import { ArenaTicketInRewardBox } from './ArenaTicketInRewardBox';
import { CardInRewardBox } from './CardInRewardBox';
import { ConversionCreditInRewardBox } from './ConversionCreditsInRewardBox';
import { DeliverableItemInRewardBox } from './DeliverableItemInRewardBox';
import { EssenceInRewardBox } from './EssenceInRewardBox';
import { ShopItemInRewardBox } from './ShopItemInRewardBox';
import {
  RewardBoxContent_ProbabilisticBundleSlotInterface,
  openRewardBoxMutation,
  openRewardBoxMutationVariables,
  useFormatBox_ProbabilisticBundle,
} from './__generated__/utils.graphql';

const HandledRewards: StuffInRewardBoxType[] = [
  CardInRewardBox,
  ShopItemInRewardBox,
  ArenaTicketInRewardBox,
  DeliverableItemInRewardBox,
  ConversionCreditInRewardBox,
  EssenceInRewardBox,
];

const getItemComponent = (
  item: RewardBoxContent_ProbabilisticBundleSlotInterface
) => {
  return HandledRewards.find(component => component.canHandle(item));
};

export const getPreloaders = (
  items: RewardBoxContent_ProbabilisticBundleSlotInterface[]
) => {
  return items
    .map(item => {
      const Preloader = getItemComponent(item)?.preloader;
      if (!Preloader) return null;
      return <Preloader key={item.id} item={item} visible={false} />;
    })
    .filter(Boolean);
};

export const getFallbacks = (
  items: RewardBoxContent_ProbabilisticBundleSlotInterface[]
) => {
  return items
    .map(item => {
      const fallback = getItemComponent(item)?.fallback;
      if (!fallback) return null;
      return fallback(item);
    })
    .filter(Boolean);
};

const OPEN_REWARD_BOX_MUTATION = gql`
  mutation openRewardBoxMutation($id: ID!, $sport: Sport!) {
    probabilisticBundlesOpen(input: { probabilisticBundleId: $id }) {
      probabilisticBundle {
        id
        opened
        openingDisclaimers {
          slug
          message
        }
      }
      currentUser {
        slug
        unopenedProbabilisticBundles(sport: $sport) {
          totalCount
        }
        cardShardsChests(sport: $sport) {
          id
          rarity
          cardShardsCount
          newThresholdUnlocked
        }
        ...UnopenedRewardBoxes_CurrentUser
      }
      errors {
        code
        message
      }
    }
  }
  ${UNOPENED_REWARD_BOXES}
` as TypedDocumentNode<openRewardBoxMutation, openRewardBoxMutationVariables>;

export const REWARD_BOX_CONTENT_FRAGMENT = gql`
  fragment RewardBoxContent_ProbabilisticBundleSlotInterface on ProbabilisticBundleSlotItemInterface {
    id
    ...CardInRewardBox_ProbabilisticBundleSlotItemInterface
    ...ShopItemInRewardBox_ProbabilisticBundleSlotItemInterface
    ...ArenaTicketInRewardBox_ProbabilisticBundleSlotItemInterface
    ...DeliverableItemInRewardBox_ProbabilisticBundleSlotItemInterface
    ...ConversionCreditInRewardBox_ProbabilisticBundleSlotItemInterface
    ...EssenceInRewardBox_ProbabilisticBundleSlotItemInterface
  }
  ${CardInRewardBox.fragments.ProbabilisticBundleSlotItemInterface}
  ${ShopItemInRewardBox.fragments.ProbabilisticBundleSlotItemInterface}
  ${ArenaTicketInRewardBox.fragments.ProbabilisticBundleSlotItemInterface}
  ${DeliverableItemInRewardBox.fragments.ProbabilisticBundleSlotItemInterface}
  ${ConversionCreditInRewardBox.fragments.ProbabilisticBundleSlotItemInterface}
  ${EssenceInRewardBox.fragments.ProbabilisticBundleSlotItemInterface}
` as TypedDocumentNode<RewardBoxContent_ProbabilisticBundleSlotInterface>;

type UseFormatBoxOptions = {
  onClaimed?: (id: string) => void;
};
export const useFormatBox = withFragments(
  (
    rewardBoxes: useFormatBox_ProbabilisticBundle[],
    opts?: UseFormatBoxOptions
  ): RewardBox[] => {
    const { onClaimed } = opts ?? {};
    const [openBoxMutation] = useMutation(OPEN_REWARD_BOX_MUTATION);
    const track = useEvents();
    const { sport } = useSportContext();

    return rewardBoxes.map<RewardBox>(box => {
      return {
        id: box.id,
        type: box.config.design.toString(),
        boxColor: BOX_COLOR[box.config.quality],
        frameColor: FRAME_COLOR[box.config.quality],
        neonColor: box.config.neonColor,
        rewardColor: box.items.at(0)?.neonColor || '',
        intensity: BOX_LIGHT_INTENSITY[box.items.at(0)?.intensity || 'DEFAULT'],
        division: box.config.division ?? undefined,
        stars: box.config.stars ?? undefined,
        fileName:
          (box.items[0] &&
            getItemComponent(box.items[0])?.description(box.items[0])) ||
          'Reward',
        onClaim: async () => {
          track('Open Reward Box', { sport, rewardBoxId: box.id });
          await openBoxMutation({ variables: { id: box.id, sport } });
          onClaimed?.(box.id);
        },
        opened: box.opened,
        children: box.items.flatMap(item => {
          const Component = getItemComponent(item);
          if (Component === undefined) return null;
          return <Component key={item.id} item={item} visible={false} />;
        }),
        isOpenable: box.isOpenable,
        openableAt: box.openableAt,
      };
    });
  },
  {
    ProbabilisticBundle: gql`
      fragment useFormatBox_ProbabilisticBundle on ProbabilisticBundle {
        id
        opened
        config {
          id
          design
          neonColor
          quality
          division
          stars
        }
        isOpenable
        openableAt
        items {
          id
          neonColor
          intensity
          ...RewardBoxContent_ProbabilisticBundleSlotInterface
        }
      }
      ${REWARD_BOX_CONTENT_FRAGMENT}
    ` as TypedDocumentNode<useFormatBox_ProbabilisticBundle>,
  }
);
